import { ReactNode, useState } from "react";
import { Box, Drawer, Fab, Typography, useTheme } from "@mui/material";
import { useNavigate } from "../../../../modules/common";
import {
  LargeCustomIcon,
  MediumCustomIcon,
} from "../../../../modules/components/layer00";
import {
  NavBarItem,
  INavBarItem,
} from "../../../../modules/components/layer01";

type MobileNavBarProps = {
  navGroups: {
    label: string;
    items: INavBarItem[];
  }[];
  bottomBottom?: {
    label: string;
    icon: ReactNode;
    path?: string;
    onClick: () => void;
    loading?: boolean;
  };
  extraSection?: ReactNode;
};

export const MobileNavBar = (props: MobileNavBarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const navBackground = isDark
    ? "var(--team-app-gradient-stripe-dark)"
    : `repeating-linear-gradient(-45deg, white 0px 5px, var(--team-app-stripe-light) 5px 6px)`;

  return (
    <>
      {!open && (
        <Fab
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: "1rem",
            bottom: "1rem",
            borderRadius: "1rem",
            zIndex: theme.zIndex.drawer - 1,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <LargeCustomIcon icon="MOBILEMENU" stroke="white" />
        </Fab>
      )}

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
            overflowY: "auto",
            borderRadius: "1rem 1rem 0 0",
            paddingTop: "1.5rem",
            paddingRight: "1rem",
            background: navBackground,
          },
        }}
      >
        {props.navGroups.map((group) => (
          <Box key={group.label} paddingBottom="1.5rem">
            <Typography
              variant="navBarGroupTitle"
              paddingLeft="1.75rem"
              sx={{
                display: "block",
              }}
            >
              {group.label}
            </Typography>
            <Box paddingTop=".5rem">
              {group.items.map((item) => (
                <NavBarItem
                  key={item.path ?? item.label}
                  isOpen={true}
                  label={item.label}
                  icon={item.icon}
                  path={item.path}
                  disabled={item.disabled}
                  onClick={() => {
                    if (!item.path) return;
                    navigate(item.path ?? "", {
                      id: item.path,
                    });
                    setOpen(false);
                  }}
                />
              ))}
            </Box>
          </Box>
        ))}

        {props.extraSection}

        {/* Pinned footer so the bottom button and close button stay visible while the nav scrolls. */}
        <Box
          position="sticky"
          bottom={0}
          display="flex"
          alignItems="center"
          columnGap=".5rem"
          marginTop="auto"
          paddingY="1rem"
          sx={{ background: navBackground }}
        >
          {props.bottomBottom && (
            <Box
              flex="1 1 0"
              minWidth={0}
              onClick={() => {
                if (props.bottomBottom?.loading) return;
                props.bottomBottom?.onClick();
                setOpen(false);
              }}
            >
              <NavBarItem
                label={props.bottomBottom.label}
                icon={props.bottomBottom.icon}
                path={props.bottomBottom.path}
                isButton={true}
                loading={props.bottomBottom.loading}
              />
            </Box>
          )}

          <Fab
            onClick={() => setOpen(false)}
            className={isDark ? "bg-teamApp-dark-surface-hover" : undefined}
            sx={{
              marginLeft: "auto",
              flexShrink: 0,
              borderRadius: "1rem",
              backgroundColor: isDark ? undefined : theme.palette.grey["800"],
              "&:hover": {
                backgroundColor: isDark
                  ? "var(--team-app-dark-surface-active)"
                  : theme.palette.grey["900"],
              },
              "& path": {
                stroke: "var(--team-app-stripe-mid)",
              },
            }}
          >
            <MediumCustomIcon icon="CLOSE" />
          </Fab>
        </Box>
      </Drawer>
    </>
  );
};
